import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface PhotoSectionProps {
  photos: string[];
  isOpen: boolean;
}

export default function PhotoSection({ photos, isOpen }: PhotoSectionProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Fermer la lightbox avec la touche Échap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") prevPhoto();
      if (e.key === "ArrowRight") nextPhoto();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex]);

  const nextPhoto = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) =>
      prev === photos.length - 1 ? 0 : (prev ?? 0) + 1
    );
  };

  const prevPhoto = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) =>
      prev === 0 ? photos.length - 1 : (prev ?? 0) - 1
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="overflow-hidden mt-4"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Grille d’images */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((photo, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.05 }}
                className="cursor-pointer overflow-hidden shadow-md relative"
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={photo}
                  alt={`preview_${idx + 1}`}
                  className="w-full h-48 md:h-64 object-cover transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>

          {/* Lightbox */}
          <AnimatePresence>
            {lightboxIndex !== null && (
              <motion.div
                className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setLightboxIndex(null)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(null);
                  }}
                  className="absolute top-6 right-6 text-white hover:text-orange transition"
                >
                  <X size={32} />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevPhoto();
                  }}
                  className="absolute left-6 text-white hover:text-orange transition"
                >
                  <ChevronLeft size={48} />
                </button>

                <motion.img
                  key={lightboxIndex}
                  src={photos[lightboxIndex]}
                  alt={`Photo agrandie ${lightboxIndex + 1}`}
                  className="max-h-[90vh] max-w-[90vw] shadow-lg object-contain"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  onClick={(e) => e.stopPropagation()}
                />

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextPhoto();
                  }}
                  className="absolute right-6 text-white hover:text-orange transition"
                >
                  <ChevronRight size={48} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}